/**
 * Shared helpers for the SCE multi-year capacity outlook (Available Load Capacity
 * for circuits, available substation capacity for substations).
 *
 * The data shape is `{ plan_year, estimated_available: [y1..y5], ... }` where the
 * series is positional: index `i` corresponds to calendar year `plan_year + i`.
 *
 * Instead of assuming a fixed "plan_year + 1" year, we anchor on the *current*
 * calendar year: the current-year index is `currentYear - plan_year`, clamped to
 * the available range. This keeps the headline value and the outlook window
 * correct as time passes without re-importing the source data.
 */

export const getCurrentYear = () => new Date().getFullYear();

const parsePlanYear = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Index into a positional multi-year series for the current calendar year.
 * Returns null when plan_year is unknown or the series is empty.
 */
export const currentYearIndex = (planYear, seriesLength) => {
  const py = parsePlanYear(planYear);
  if (py == null || !Number.isFinite(seriesLength) || seriesLength <= 0) return null;
  const idx = getCurrentYear() - py;
  return Math.min(seriesLength - 1, Math.max(0, idx));
};

/**
 * Up to `count` indices starting at the current-year index (current year first,
 * then the following years that exist in the series). Returns [] when undeterminable.
 */
export const outlookIndices = (planYear, seriesLength, count = 4) => {
  const start = currentYearIndex(planYear, seriesLength);
  if (start == null) return [];
  const indices = [];
  for (let i = start; i < seriesLength && indices.length < count; i += 1) {
    indices.push(i);
  }
  return indices;
};

/**
 * Current-year value from a positional series, or null when unavailable.
 * `series[i]` maps to year `plan_year + i`.
 */
export const currentYearValue = (planYear, series) => {
  if (!Array.isArray(series) || series.length === 0) return null;
  const idx = currentYearIndex(planYear, series.length);
  if (idx == null) return null;
  const value = series[idx];
  return value == null ? null : value;
};

/**
 * Parse the `alc_multiyear` tile/API payload. MVT stores it as a JSON string;
 * API responses may already be an object.
 */
export const parseAlcMultiyear = (raw) => {
  if (raw == null || raw === '') return null;
  let outlook = raw;
  if (typeof raw === 'string') {
    try {
      outlook = JSON.parse(raw);
    } catch (_) {
      return null;
    }
  }
  if (!outlook || typeof outlook !== 'object') return null;
  return outlook;
};

const formatOutlookMw = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  return `${num.toFixed(2).replace(/\.?0+$/, '')} MW`;
};

/**
 * Current-year-anchored outlook entries for a positional MW series.
 * Returns `[{ year, mw }]` (mw already formatted, e.g. "52.98 MW").
 */
export const seriesOutlookEntries = (planYear, series) => {
  if (!Array.isArray(series) || series.length === 0) return [];
  const py = Number(planYear);
  if (!Number.isFinite(py)) return [];
  const entries = [];
  outlookIndices(py, series.length).forEach((i) => {
    const mw = formatOutlookMw(series[i]);
    if (mw) entries.push({ year: py + i, mw });
  });
  return entries;
};

/**
 * Format outlook entries as "2026: 4.1 MW  |  2027: 4.1 MW ...".
 */
export const formatOutlookEntriesLine = (entries) =>
  (Array.isArray(entries) ? entries : [])
    .map((e) => `${e.year}: ${e.mw}`)
    .join('  |  ');

/**
 * Circuit Available Load Capacity display fields from an SCE `alc_multiyear`
 * snapshot. Headline capacity is the current calendar year.
 */
export const circuitAlcDisplay = (rawAlc) => {
  const outlook = parseAlcMultiyear(rawAlc);
  if (!outlook) {
    return { currentMw: null, outlookEntries: [] };
  }
  return {
    currentMw: formatOutlookMw(
      currentYearValue(outlook.plan_year, outlook.estimated_available)
    ),
    outlookEntries: seriesOutlookEntries(outlook.plan_year, outlook.estimated_available),
  };
};

/**
 * A Bank (subtransmission transformer) display fields from an SCE circuit
 * `alc_multiyear` snapshot. Headline capacity is the current calendar year.
 */
export const A_BANK_DESCRIPTION =
  'An A-Bank is SCE’s subtransmission transformer (typically 220/66 kV) that supplies the distribution substation feeding this circuit. Available A-Bank capacity can limit new load even when the circuit itself has headroom.';

export const circuitABankDisplay = (rawAlc) => {
  const outlook = parseAlcMultiyear(rawAlc);
  if (!outlook) {
    return { name: null, currentMw: null, outlookEntries: [] };
  }
  const name = String(outlook.a_bank ?? '').trim() || null;
  const currentMw = formatOutlookMw(
    currentYearValue(outlook.plan_year, outlook.a_bank_estimated_available)
  );
  return {
    name,
    currentMw,
    outlookEntries: seriesOutlookEntries(outlook.plan_year, outlook.a_bank_estimated_available),
  };
};
